import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@reactuses/core";
import { useQuery } from "@tanstack/react-query";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { useAppSelector } from "../../../redux/hooks";
import { css } from "../../../../styled-system/css";
import {
  type ManagedTableState,
  type ManagedTableStateSetters,
} from "../../../components/table/usePersistedTableState";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table, TableBanner } from "../../../components/table/Table";
import { resetTable, selectionStateToSelectedIds } from "../../../components/table/utils";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { DataViewPicker } from "../../dataViews/components/DataViewPicker";
import { Button } from "../../../components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { DownloadV4Report } from "../DownloadReport";
import { type TargetQueryV4Row, useGetV4TargetReportByTarget } from "../api/target/v4TargetReportByTarget";
import { useV4TargetReportExport } from "../api/target/v4TargetReportExport";
import TargetDetails from "../../advertising/components/TargetDetails";
import {
  AD_STATE_OPTIONS,
  AMAZON_TARGET_SOURCE_OPTIONS,
  BID_TREND_OPTIONS,
  TARGET_MATCH_TYPE_OPTIONS,
  TARGET_STATE_OPTIONS,
  WALMART_TARGET_SOURCE_OPTIONS,
} from "../../advertising/filters/targetLifecycleFilters";
import { BulkActionButton } from "../../../components/table/BulkActionButton";
import useTargetBulkActions from "../../../hooks/useTargetBulkActions";
import type { ColumnFiltersState, RowSelectionState, Updater } from "@tanstack/react-table";
import { useGetV4AdvReportByAd } from "../api/ads/v4AdvReportByAd";
import { useTagFilterOptions } from "../../tags/api/useTagFilterOptions";
import { useTagNameFilterOptions } from "../../tags/api/useTagNameFilterOptions";
import { useGetV4TargetReportOptions } from "../api/target/v4TargetReportOptions";
import { useSimpleCan } from "../../../config/casl/casl";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { EditableTableWrapper, EditableTableSubmitArea } from "../../../components/table/EditableTableWrapper";
import { TableErrorWrapper } from "../../../components/table/TableErrorWrapper";
import { useUpdateTargetBids } from "../../../hooks/useUpdateTargetBids";
import { DIMENSION_TYPES, useTargetBidStateOptions } from "../../str/utils/reportingConstants";
import { AD_STATUSES } from "../../../components/core/blocks/AdPlan/constants";
import { useServerTable, type AppRow, type AppColumns } from "../../../components/table/tableFeatures";

const EMPTY_FILTERS: ColumnFiltersState = [];

type ProductTargetReportTableProps = {
  columns: AppColumns<TargetQueryV4Row>;
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  categoryFilterOptions?: { value: number | string; label: string }[];
  parentFilterControls?: React.ReactNode;
  extraFilters?: ColumnFiltersState;
  disableExpander?: boolean;
  renderDownloadButton?: boolean;
};

export const ProductTargetReportTable = ({
  columns,
  state,
  initialState,
  setters,
  categoryFilterOptions = [],
  parentFilterControls = null,
  extraFilters = EMPTY_FILTERS,
  disableExpander = false,
  renderDownloadButton = true,
}: ProductTargetReportTableProps) => {
  const { t } = useTranslation(["perf_reports"]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedRowsData, setSelectedRowsData] = useState<Record<string, unknown>>({});

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const userCanExportAds = useSimpleCan("export", "Ad");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const targetBidStateOptions = useTargetBidStateOptions();

  const byTargetOptionsGetter = useGetV4TargetReportByTarget("target", DIMENSION_TYPES.TARGET_ID);
  const combinedFilters = useMemo(() => {
    return [...extraFilters, ...state.columnFilters];
  }, [extraFilters, state.columnFilters]);
  const targetByTargetReportQuery = useBlockWindowQuery(byTargetOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: combinedFilters,
    search: debounceSearch,
  });

  const byAdGetter = useGetV4AdvReportByAd();

  const adIdsOnPage = useMemo(() => {
    return Array.from(new Set((targetByTargetReportQuery.data?.data ?? []).map((row: TargetQueryV4Row) => row.ad_id)));
  }, [targetByTargetReportQuery.data?.data]);

  const advertisementQuery = useQuery({
    ...byAdGetter({
      pagination: { pageIndex: 0, pageSize: adIdsOnPage.length },
      sorting: [],
      filters: adIdsOnPage.length > 0 ? [{ id: "ad_id", value: { op: "anyof", value: adIdsOnPage } }] : [],
      search: "",
    }),
    enabled: adIdsOnPage.length > 0,
  });

  const isAdsDataLoading =
    adIdsOnPage.length > 0 &&
    advertisementQuery.isFetching &&
    (!advertisementQuery.data || advertisementQuery.isPlaceholderData);

  const adsMapping = useMemo(() => {
    const ads = advertisementQuery.data?.data ?? [];
    const mapping: Record<string, { ad_id: number; ad_name: string; managed: string; ad_type?: number }> = {};
    for (const ad of ads) {
      mapping[ad.ad_id.toString()] = { ad_id: ad.ad_id, ad_name: ad.ad_name, managed: ad.managed, ad_type: ad.ad_type };
    }
    return mapping;
  }, [advertisementQuery.data?.data]);

  type ExtendedTargetQueryV4Row = TargetQueryV4Row & { managed?: boolean };

  const tableData: ExtendedTargetQueryV4Row[] = useMemo(() => {
    if (targetByTargetReportQuery.data?.data) {
      const data = targetByTargetReportQuery.data.data.map((target: TargetQueryV4Row) => {
        const ad = adsMapping[target.ad_id];

        return { ...target, managed: ad?.managed.toLowerCase() === "yes" };
      });
      return data;
    }

    return [];
  }, [targetByTargetReportQuery.data?.data, adsMapping]);

  const unmanagedTargetIds = useMemo(() => {
    return tableData
      .filter((target) => !target.managed)
      .map((target) => target[DIMENSION_TYPES.TARGET_ID])
      .filter((targetId): targetId is number => typeof targetId === "number");
  }, [tableData]);

  const lockedCount = targetByTargetReportQuery.data?.count_details?.locked_count ?? 0;

  const pageLockedCount = useMemo(() => {
    return tableData.filter((row) => row.lockout).length;
  }, [tableData]);

  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    const newRowSelection = typeof updater === "function" ? updater(rowSelection) : updater;

    const updatedSelectedRowsData = { ...selectedRowsData };

    for (const row of table.getRowModel().rows) {
      const id = row.id;

      if (newRowSelection[id]) {
        updatedSelectedRowsData[id] = {
          tags: row.original.tags,
          target_source: row.original.source,
          scheduled_actions: row.original.scheduled_actions,
          id: parseInt(id),
          target_id: parseInt(id),
        };
      } else {
        // oxlint-disable-next-line typescript/no-dynamic-delete
        delete updatedSelectedRowsData[id];
      }
    }

    setRowSelection(newRowSelection);
    setSelectedRowsData(updatedSelectedRowsData);
  };

  const exportMutation = useV4TargetReportExport("target", combinedFilters);
  const { submitBids } = useUpdateTargetBids("target");
  const tagEnhancedOptions = useTagFilterOptions("KEYWORD-TARGET", { enabled: tagsEnhancedEnabled });
  const targetReportOptionsQuery = useQuery({
    ...useGetV4TargetReportOptions("target"),
    enabled: !tagsEnhancedEnabled,
  });
  const tagOptions = useTagNameFilterOptions(targetReportOptionsQuery.data?.data.tags);

  const table = useServerTable({
    data: tableData,
    columns,
    alternateSortingEnabled: true,
    getRowCanExpand: (row) => {
      if (disableExpander) {
        return false;
      }
      return typeof row.original[DIMENSION_TYPES.TARGET_ID] === "number";
    },
    getRowId: (row, index) => {
      return row.adv_target_id?.toString() ?? `row-${index}`;
    },
    enableRowSelection: (row) => {
      if (row.original.lockout) {
        return false;
      }

      const idValue = row.original[DIMENSION_TYPES.TARGET_ID];
      return typeof idValue === "number" && !unmanagedTargetIds.includes(idValue);
    },
    rowCount: targetByTargetReportQuery.data?.count || 0,
    initialState: initialState,
    onRowSelectionChange: handleRowSelectionChange,
    state: { ...state, rowSelection },
    ...Object.fromEntries(Object.entries(setters).filter(([key]) => key !== "onRowSelectionChange")),
    enableSelectAllMatching: true,
    meta: {
      showSkeleton:
        (targetByTargetReportQuery.isFetching &&
          (!targetByTargetReportQuery.data || targetByTargetReportQuery.isPlaceholderData)) ||
        isAdsDataLoading,
      lockedCount: lockedCount,
      pageLockedCount,
      getRowSelectionDisabledReason: (row: AppRow<ExtendedTargetQueryV4Row>) => {
        if (row.original.lockout) {
          return t(
            ($) =>
              $.perf_reports[
                "This product target is locked because it already has an update pending from a bulk action. It will be selectable again once that update completes."
              ],
          );
        }
        const idValue = row.original[DIMENSION_TYPES.TARGET_ID];
        if (typeof idValue === "number" && unmanagedTargetIds.includes(idValue)) {
          return t(($) => $.perf_reports["This is an unmanaged target, it can't be selected for bulk actions."]);
        }
        return undefined;
      },
      columnFilterSelectOptions: {
        state: TARGET_STATE_OPTIONS,
        match_type: TARGET_MATCH_TYPE_OPTIONS,
        category_id: categoryFilterOptions,
        has_manual_bid: [
          { value: true, label: t(($) => $.perf_reports["Has Fixed Bid"]) },
          { value: false, label: t(($) => $.perf_reports["No Fixed Bid"]) },
        ],
        source: channel === "walmart" ? WALMART_TARGET_SOURCE_OPTIONS : AMAZON_TARGET_SOURCE_OPTIONS,
        bid_trend_latest: BID_TREND_OPTIONS,
        bid_trend_slope: BID_TREND_OPTIONS,
        target_bid_state: targetBidStateOptions,
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        ad_status: [
          { value: "all", label: t(($) => $.perf_reports["All Statuses"]) },
          ...Object.values(AD_STATUSES).filter((status) => status !== AD_STATUSES.GV_ADV_ARCHIVED),
        ],
        ad_state: AD_STATE_OPTIONS,
      },
      adsMapping,
    } as {
      showSkeleton?: boolean;
      lockedCount?: number;
      columnFilterSelectOptions?: Record<
        string,
        { label: string | ((...args: unknown[]) => unknown); value: unknown; description?: string }[]
      >;
      adsMapping?: Record<string, { ad_id: number; ad_name: string; managed: string; ad_type?: number }>;
    },
  });

  const selectedIds = selectionStateToSelectedIds(table.state.rowSelection).map((id) => parseInt(id));
  const selected = state.isAllMatchingSelected ? ("all" as const) : selectedIds;

  const resetSelection = () => {
    table.resetRowSelection();
    setSelectedRowsData({});
  };

  const { bulkActionOptions } = useTargetBulkActions(
    selected,
    resetSelection,
    "target",
    { keywords: Object.values(selectedRowsData) },
    false,
    true,
    { filters: combinedFilters, sorting: state.sorting, grouping: DIMENSION_TYPES.TARGET_ID, search: debounceSearch },
  );

  return (
    <EditableTableWrapper formOptions={{ readPretty: !userCanUpdateAds }}>
      <div>
        <div className={css({ maxWidth: "88vw" })}>
          <Wrap
            className={css({
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              maxWidth: "100%",
              padding: "small",
              fontSize: "large",
            })}
          >
            <HStack className={css({ justifyContent: "flex-end", width: "100%" })}>
              <BulkActionButton
                options={bulkActionOptions}
                selected={selected}
                selectedCount={state.isAllMatchingSelected ? table.getRowCount() - lockedCount : undefined}
                isDisabled={!userCanUpdateAds}
              />
            </HStack>
            {userCanUpdateAds && <EditableTableSubmitArea<{ bid_resolved?: number }> onSubmit={submitBids} />}
          </Wrap>
          <HStack width={"100%"} padding={"small"} fontSize={"large"}>
            <VStack flex="1" alignItems={"start"}>
              <HStack width={"100%"}>
                <Button
                  variant={"contentOnly"}
                  onPress={() => resetTable(table, "productTargetReport")}
                  fontSize={"xxlarge"}
                >
                  <MdRotateLeft />
                </Button>
                <RT8ColumnSelector table={table} />
                <DataViewPicker viewKey="productTargetReport" datasetKey="target" table={table} />
                <SearchInput
                  aria-label={t(($) => $.perf_reports["Search"])}
                  isExpandable
                  css={{ flex: "0 1 350px" }}
                  value={table.state.globalFilter}
                  onChange={table.setGlobalFilter}
                />
                {parentFilterControls}
              </HStack>
              <RT8FilterWidget table={table} />
            </VStack>
            <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
          </HStack>
        </div>
        <TableErrorWrapper
          error={targetByTargetReportQuery.error}
          userMessage={t(($) => $.perf_reports["Error loading stats"])}
        >
          {pageLockedCount > 0 && !table.getIsAllRowsSelected() && !state.isAllMatchingSelected && (
            <TableBanner>
              {t(
                ($) =>
                  $.perf_reports[
                    "{count, plural, one {# row} other {# rows}} on this page {count, plural, one {has} other {have}} updates in progress and cannot be selected."
                  ],
                { count: pageLockedCount },
              )}
            </TableBanner>
          )}
          <div className={css({ width: "100%", maxWidth: "100vw", overflowX: "auto" })}>
            <Table
              table={table}
              className={css({
                width: "100%",
                minWidth: "max-content",
              })}
              renderExpander={(row) => {
                const targetId = row.original[DIMENSION_TYPES.TARGET_ID];
                const formattedRowData = {
                  _original: {
                    ...row.original,
                    target_id: targetId ? targetId : null,
                  },
                };

                return (
                  <TargetDetails
                    row={formattedRowData}
                    marketPlace={marketPlace}
                    channel={channel}
                    targetType={"target"}
                  />
                );
              }}
            />
          </div>
        </TableErrorWrapper>
        {userCanExportAds && renderDownloadButton && (
          <HStack width={"100%"} padding={"small"}>
            <DownloadV4Report downloadMutation={exportMutation} />
          </HStack>
        )}
      </div>
    </EditableTableWrapper>
  );
};
